import { useState, useEffect, createElement } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Brain, Heart, Sparkles, Zap, Search, BookOpen } from 'lucide-react';

interface GuidanceSession {
  id: string;
  session_date: string;
  question: string;
  logical_answer: string;
  logical_body_sensations: string;
  emotional_answer: string;
  emotional_body_sensations: string;
  intuitive_answer: string;
  intuitive_body_sensations: string;
  spiritual_answer: string;
  spiritual_source: string;
  synthesis: string;
  action_taken: string;
  outcome: string;
  tags: string[];
}

export default function BalancedGuidance() {
  const [sessions, setSessions] = useState<GuidanceSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    question: '',
    logical_answer: '',
    logical_body_sensations: '',
    emotional_answer: '',
    emotional_body_sensations: '',
    intuitive_answer: '',
    intuitive_body_sensations: '',
    spiritual_answer: '',
    spiritual_source: '',
    synthesis: '',
    action_taken: '',
    outcome: '',
    tags: ''
  });

  const steps = [
    { 
      icon: Brain, 
      title: 'Logical Mind', 
      color: 'text-accent',
      instruction: 'Place hands on your head. Take 3 deep breaths. Connect with your mind, body, and soul by sitting in the power. Ask for a logical solution to your question.',
      fields: ['logical_answer', 'logical_body_sensations']
    },
    { 
      icon: Heart, 
      title: 'Emotional Heart', 
      color: 'text-ring',
      instruction: 'Take 3 deep breaths. Reconnect. Place hands on your heart. Ask for an emotional answer. Feel into a yes or no response if that\'s easier.',
      fields: ['emotional_answer', 'emotional_body_sensations']
    },
    { 
      icon: Zap, 
      title: 'Intuitive Belly', 
      color: 'text-secondary',
      instruction: 'Take 3 deep breaths. Reconnect. Place hands on your belly. Ask for an intuitive answer. Trust your gut feelings.',
      fields: ['intuitive_answer', 'intuitive_body_sensations']
    },
    { 
      icon: Sparkles, 
      title: 'Spiritual Guidance', 
      color: 'text-muted',
      instruction: 'Take 3 deep breaths. Reconnect. Hands crossed over heart, expanding as when sitting in the power. Connect with your higher self, spirit guide, or loved one. Greet them, thank them, ask for spiritual guidance. Trust the first thing that comes.',
      fields: ['spiritual_answer', 'spiritual_source']
    },
    { 
      icon: BookOpen, 
      title: 'Synthesis', 
      color: 'text-accent',
      instruction: 'Review all four answers. What patterns emerge? What feels most aligned? Create a balanced synthesis drawing from all perspectives.',
      fields: ['synthesis']
    }
  ];

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('balanced_guidance_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('session_date', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load guidance sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        return;
      }

      const tags = formData.tags.split(',').map(t => t.trim()).filter(t => t);

      const { error } = await supabase
        .from('balanced_guidance_sessions')
        .insert({
          user_id: user.id,
          question: formData.question,
          logical_answer: formData.logical_answer,
          logical_body_sensations: formData.logical_body_sensations,
          emotional_answer: formData.emotional_answer,
          emotional_body_sensations: formData.emotional_body_sensations,
          intuitive_answer: formData.intuitive_answer,
          intuitive_body_sensations: formData.intuitive_body_sensations,
          spiritual_answer: formData.spiritual_answer,
          spiritual_source: formData.spiritual_source,
          synthesis: formData.synthesis,
          action_taken: formData.action_taken,
          outcome: formData.outcome,
          tags
        });

      if (error) throw error;

      toast.success('Guidance session saved!');
      setShowForm(false);
      setCurrentStep(0);
      setFormData({
        question: '',
        logical_answer: '',
        logical_body_sensations: '',
        emotional_answer: '',
        emotional_body_sensations: '',
        intuitive_answer: '',
        intuitive_body_sensations: '',
        spiritual_answer: '',
        spiritual_source: '',
        synthesis: '',
        action_taken: '',
        outcome: '',
        tags: ''
      });
      loadSessions();
    } catch (error) {
      console.error('Error saving session:', error);
      toast.error('Failed to save guidance session');
    }
  };

  const filteredSessions = sessions.filter(session => 
    session.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return <div className="text-muted-foreground">Loading guidance sessions...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-secondary/10 to-accent/10 border-accent/20">
        <h2 className="text-2xl font-bold mb-2">Balanced Self-Guidance</h2>
        <p className="text-muted-foreground mb-4">
          Connect with all parts of yourself - mind, heart, intuition, and spirit - to receive balanced guidance for your questions and decisions.
        </p>

        <div className="bg-background/50 rounded-lg p-4 mb-4 space-y-2 text-sm">
          <p className="font-semibold">The Practice:</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>• Take 3 deep breaths before each step</li>
            <li>• Sit in the power - connect mind, body, energy body</li>
            <li>• Place hands as directed for each perspective</li>
            <li>• Note body sensations - they're inner-tuition speaking</li>
            <li>• Trust first impressions, even if they feel silly</li>
            <li>• Your intention is enough; connection grows with practice</li>
          </ul>
        </div>

        <Button onClick={() => setShowForm(!showForm)} className="w-full bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">
          {showForm ? 'Cancel' : 'Start Guidance Session'}
        </Button>
      </Card>

      {showForm && (
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {currentStep === 0 && (
              <div className="space-y-4">
                <div>
                  <Label>Your Question or Decision</Label>
                  <Textarea
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    placeholder="What guidance do you need?"
                    rows={3}
                    required
                  />
                </div>
                  <Button type="button" onClick={() => setCurrentStep(1)} disabled={!formData.question} className="bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">
                  Begin Guidance Process
                </Button>
              </div>
            )}

            {currentStep > 0 && currentStep < 5 && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  {createElement(steps[currentStep - 1].icon, { 
                    className: `h-8 w-8 ${steps[currentStep - 1].color}` 
                  })}
                  <div>
                    <h3 className="font-semibold text-lg">{steps[currentStep - 1].title}</h3>
                    <p className="text-sm text-muted-foreground">{steps[currentStep - 1].instruction}</p>
                  </div>
                </div>

                {currentStep === 1 && (
                  <>
                    <div>
                      <Label>Logical Answer</Label>
                      <Textarea
                        value={formData.logical_answer}
                        onChange={(e) => setFormData({ ...formData, logical_answer: e.target.value })}
                        placeholder="What does your logical mind say?"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Body Sensations</Label>
                      <Textarea
                        value={formData.logical_body_sensations}
                        onChange={(e) => setFormData({ ...formData, logical_body_sensations: e.target.value })}
                        placeholder="High-pitched sounds, pains, flashes of light, tingling...?"
                        rows={2}
                      />
                    </div>
                  </>
                )}

                {currentStep === 2 && (
                  <>
                    <div>
                      <Label>Emotional Answer</Label>
                      <Textarea
                        value={formData.emotional_answer}
                        onChange={(e) => setFormData({ ...formData, emotional_answer: e.target.value })}
                        placeholder="What does your heart feel? (yes/no is fine)"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Body Sensations</Label>
                      <Textarea
                        value={formData.emotional_body_sensations}
                        onChange={(e) => setFormData({ ...formData, emotional_body_sensations: e.target.value })}
                        placeholder="What sensations arise in your body?"
                        rows={2}
                      />
                    </div>
                  </>
                )}

                {currentStep === 3 && (
                  <>
                    <div>
                      <Label>Intuitive Answer</Label>
                      <Textarea
                        value={formData.intuitive_answer}
                        onChange={(e) => setFormData({ ...formData, intuitive_answer: e.target.value })}
                        placeholder="What does your gut instinct say?"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Body Sensations</Label>
                      <Textarea
                        value={formData.intuitive_body_sensations}
                        onChange={(e) => setFormData({ ...formData, intuitive_body_sensations: e.target.value })}
                        placeholder="What do you feel in your belly/gut area?"
                        rows={2}
                      />
                    </div>
                  </>
                )}

                {currentStep === 4 && (
                  <>
                    <div>
                      <Label>Spiritual Answer</Label>
                      <Textarea
                        value={formData.spiritual_answer}
                        onChange={(e) => setFormData({ ...formData, spiritual_answer: e.target.value })}
                        placeholder="What guidance comes from your higher self/guide/loved one?"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Source (optional)</Label>
                      <Input
                        value={formData.spiritual_source}
                        onChange={(e) => setFormData({ ...formData, spiritual_source: e.target.value })}
                        placeholder="Who stepped forward? (guide name, loved one, higher self)"
                      />
                    </div>
                  </>
                )}

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
                    Back
                  </Button>
                  <Button type="button" onClick={() => setCurrentStep(currentStep + 1)} className="bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">
                    Next
                  </Button>
                </div>
              </div>
            )}

            {currentStep === 5 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="font-semibold text-sm">Logical:</p>
                    <p className="text-sm text-muted-foreground">{formData.logical_answer}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Emotional:</p>
                    <p className="text-sm text-muted-foreground">{formData.emotional_answer}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Intuitive:</p>
                    <p className="text-sm text-muted-foreground">{formData.intuitive_answer}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Spiritual:</p>
                    <p className="text-sm text-muted-foreground">{formData.spiritual_answer}</p>
                  </div>
                </div>

                <div>
                  <Label>Your Synthesis</Label>
                  <Textarea
                    value={formData.synthesis}
                    onChange={(e) => setFormData({ ...formData, synthesis: e.target.value })}
                    placeholder="What balanced wisdom emerges from all four perspectives?"
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <Label>Tags (comma separated)</Label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="e.g., career, relationship, health"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setCurrentStep(4)}>
                    Back
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">Save Session</Button>
                </div>
              </div>
            )}
          </form>

          {/* Step indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {[0, 1, 2, 3, 4, 5].map((step) => (
              <div
                key={step}
                className={`h-2 w-8 rounded-full ${
                  step <= currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Search */}
      <div className="flex items-center gap-2">
        <Search className="h-5 w-5 text-muted-foreground" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by question or tag..."
          className="flex-1"
        />
      </div>

      {/* Past Sessions */}
      <div className="space-y-3">
        <h3 className="font-semibold">Past Guidance Sessions</h3>
        {filteredSessions.length === 0 ? (
          <Card className="p-6 text-center text-muted-foreground">
            <p>No guidance sessions yet. Start your first session to receive balanced wisdom from all parts of yourself.</p>
          </Card>
        ) : (
          filteredSessions.map(session => (
            <Card key={session.id} className="p-4">
              <div className="space-y-3">
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <p className="font-semibold">{session.question}</p>
                    <span className="text-xs text-muted-foreground">
                      {new Date(session.session_date).toLocaleDateString()}
                    </span>
                  </div>
                  {session.tags && session.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {session.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-primary/5 rounded p-3">
                  <p className="text-sm font-semibold mb-1">Synthesis:</p>
                  <p className="text-sm text-muted-foreground">{session.synthesis}</p>
                </div>

                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    View all perspectives
                  </summary>
                  <div className="mt-3 space-y-2 pl-4 border-l-2 border-muted">
                    {session.logical_answer && (
                      <div>
                        <p className="font-semibold flex items-center gap-1">
                          <Brain className="h-3 w-3" /> Logical:
                        </p>
                        <p className="text-muted-foreground">{session.logical_answer}</p>
                      </div>
                    )}
                    {session.emotional_answer && (
                      <div>
                        <p className="font-semibold flex items-center gap-1">
                          <Heart className="h-3 w-3" /> Emotional:
                        </p>
                        <p className="text-muted-foreground">{session.emotional_answer}</p>
                      </div>
                    )}
                    {session.intuitive_answer && (
                      <div>
                        <p className="font-semibold flex items-center gap-1">
                          <Zap className="h-3 w-3" /> Intuitive:
                        </p>
                        <p className="text-muted-foreground">{session.intuitive_answer}</p>
                      </div>
                    )}
                    {session.spiritual_answer && (
                      <div>
                        <p className="font-semibold flex items-center gap-1">
                          <Sparkles className="h-3 w-3" /> Spiritual:
                        </p>
                        <p className="text-muted-foreground">{session.spiritual_answer}</p>
                        {session.spiritual_source && (
                          <p className="text-xs text-muted-foreground italic">
                            Source: {session.spiritual_source}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </details>

                {session.action_taken && (
                  <div className="text-sm">
                    <p className="font-semibold">Action Taken:</p>
                    <p className="text-muted-foreground">{session.action_taken}</p>
                  </div>
                )}

                {session.outcome && (
                  <div className="text-sm">
                    <p className="font-semibold">Outcome:</p>
                    <p className="text-muted-foreground">{session.outcome}</p>
                  </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}